import React, { useCallback, useEffect, useState } from "react";
import { Modal, Table, Spinner } from "react-bootstrap";

const PAGE_SIZE = 10;
const SEARCH_FETCH_SIZE = 200;

/**
 * Generic single-select lookup table.
 * `config` comes from `lookupConfigs.js`:
 *   { title, idKey, columns, fetchPage(page, size) -> { data, total }, search?(q) -> rows, searchKeys? }
 */
export default function LookupModal({
  show,
  onHide,
  onSelect,
  selectedId = "",
  title,
  config,
}) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selected, setSelected] = useState(null);

  const idKey = config?.idKey ?? "_id";
  const columns = config?.columns ?? [];
  const getId = (r) => r?.[idKey];

  useEffect(() => {
    if (!show) return;
    setPage(0);
    setQuery("");
    setDebouncedQuery("");
    setSelected(selectedId ? { [idKey]: selectedId, __preselected: true } : null);
  }, [show, selectedId, idKey]);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQuery(query.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [query]);

  const load = useCallback(async () => {
    if (!config) return;
    setLoading(true);
    setError("");
    try {
      if (debouncedQuery) {
        let list = [];
        if (typeof config.search === "function") {
          list = await config.search(debouncedQuery);
        } else {
          const res = await config.fetchPage(0, SEARCH_FETCH_SIZE);
          const q = debouncedQuery.toLowerCase();
          const keys = config.searchKeys?.length ? config.searchKeys : [idKey];
          list = (Array.isArray(res?.data) ? res.data : []).filter((r) =>
            keys.some((k) => String(r?.[k] ?? "").toLowerCase().includes(q))
          );
        }
        setRows(list);
        setTotal(list.length);
      } else {
        const res = await config.fetchPage(page, PAGE_SIZE);
        setRows(Array.isArray(res?.data) ? res.data : []);
        setTotal(Number(res?.total) || 0);
      }
    } catch (err) {
      setRows([]);
      setTotal(0);
      setError(typeof err === "string" ? err : err?.message || "Failed to load records");
    } finally {
      setLoading(false);
    }
  }, [config, page, debouncedQuery, idKey]);

  useEffect(() => {
    if (show) load();
  }, [show, load]);

  const isChecked = (r) => !!selected && getId(selected) === getId(r);
  const toggle = (r) => setSelected(isChecked(r) ? null : r);

  const confirm = (record = selected) => {
    if (!record) return;
    if (!record.__preselected) onSelect?.(record);
    onHide?.();
  };

  // Enter confirms the checked row; if nothing is checked and the search
  // narrowed the list to a single row, Enter selects that row.
  const handleKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    e.stopPropagation();
    if (loading) return;
    if (getId(selected)) {
      confirm();
    } else if (rows.length === 1) {
      confirm(rows[0]);
    }
  };

  const totalPages = debouncedQuery ? 1 : Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : debouncedQuery ? 1 : page * PAGE_SIZE + 1;
  const to = debouncedQuery ? rows.length : Math.min(total, (page + 1) * PAGE_SIZE);

  return (
    <Modal show={show} onHide={onHide} size="xl" centered onKeyDown={handleKeyDown}>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6 fw-semibold">{title ?? config?.title ?? "Select"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-2" style={{ maxWidth: 320 }}>
          <div className="input-group input-group-sm">
            <span className="input-group-text">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder={config?.placeholder ?? "Search"}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
          </div>
        </div>

        <div className="table-responsive" style={{ maxHeight: 420 }}>
          <Table hover bordered size="sm" className="mb-0 align-middle">
            <thead className="table-light sticky-top">
              <tr>
                <th style={{ width: 40 }}></th>
                {columns.map((c) => (
                  <th key={c.key} className="text-nowrap">{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columns.length + 1} className="text-center py-4">
                    <Spinner animation="border" size="sm" />
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={columns.length + 1} className="text-center text-danger py-4">
                    {error}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length + 1} className="text-center text-muted py-4">
                    No records found
                  </td>
                </tr>
              ) : (
                rows.map((r, i) => (
                  <tr
                    key={r?._id ?? getId(r) ?? i}
                    className={isChecked(r) ? "table-primary" : ""}
                    style={{ cursor: "pointer" }}
                    onClick={() => toggle(r)}
                    onDoubleClick={() => confirm(r)}
                  >
                    <td className="text-center">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={isChecked(r)}
                        onChange={() => toggle(r)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    {columns.map((c) => (
                      <td key={c.key} className="text-nowrap">{String(c.get(r) ?? "")}</td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <div className="d-flex align-items-center gap-2 small text-muted">
          <span>{from}-{to} of {total}</span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={loading || page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            <i className="bi bi-chevron-left"></i>
          </button>
          <span>Page {page + 1} of {totalPages}</span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={loading || page + 1 >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            <i className="bi bi-chevron-right"></i>
          </button>
        </div>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-sm btn-secondary" onClick={onHide}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            disabled={!getId(selected)}
            onClick={() => confirm()}
          >
            Select
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
